import { apiClient } from "./client";

// ---------- KGU params ----------

export interface KguParams {
  id: string;
  tenant_id: string;
  effective_from: string; // YYYY-MM-DD

  power_min_kw: number | string;
  power_max_kw: number | string;
  power_step_kw: number | string;

  self_needs_estimate_kw: number | string;
  self_needs_off_kw: number | string;
  switching_time_min: number;

  balance_tolerance_pct: number | string;
  heat_balance_tolerance_pct: number | string;
  direction_anomaly_threshold_kwh: number | string;

  imbalance_pos_discount_pct: number | string;
  imbalance_neg_penalty_pct: number | string;

  rdn_first_attempt_at: string; // HH:MM:SS
  rdn_retry_until: string;

  period_close_day: number;

  plan_submit_deadline: string;
  plan_reminder_at: string;

  has_bess: boolean;
  auto_submit_plan_enabled: boolean;
}

export interface KguParamsWrite {
  effective_from: string;
  power_min_kw: number;
  power_max_kw: number;
  power_step_kw: number;
  self_needs_estimate_kw: number;
  self_needs_off_kw: number;
  switching_time_min: number;
  balance_tolerance_pct: number;
  heat_balance_tolerance_pct: number;
  direction_anomaly_threshold_kwh: number;
  imbalance_pos_discount_pct: number;
  imbalance_neg_penalty_pct: number;
  rdn_first_attempt_at: string;
  rdn_retry_until: string;
  period_close_day: number;
  plan_submit_deadline: string;
  plan_reminder_at: string;
  has_bess: boolean;
  auto_submit_plan_enabled: boolean;
}

// ---------- Gas curve ----------

export interface GasCurvePoint {
  id: string;
  effective_from: string;
  power_kw: number | string;
  gas_m3h: number | string;
  source: "manual" | "calibrated_from_meter";
}

export interface GasCurveWrite {
  effective_from: string;
  points: { power_kw: number; gas_m3h: number }[];
}

// ---------- Cost parameters ----------

export type CostBasis = "month" | "day" | "hour";

export interface CostParameter {
  id: string;
  tenant_id: string;
  effective_month: string; // YYYY-MM-DD (1st)
  /** null — стаття КГУ або загальнофірмова (sga_*); UUID — стаття СЕС. */
  plant_id: string | null;
  /** За який період задано value. Для gas_price не застосовується. */
  basis: CostBasis;
  code: string;
  name: string;
  value: number | string;
  unit: string;
  quantity: number | string;
  notes: string | null;
  created_by: string | null;
  created_at: string;
}

export interface CostParameterWrite {
  code: string;
  name: string;
  value: number;
  unit: string;
  quantity: number;
  basis: CostBasis;
  notes?: string | null;
}

export const parametersApi = {
  // KGU params
  getKguParams: (date?: string) =>
    apiClient
      .get<KguParams>("/kgu-params", { params: date ? { date } : undefined })
      .then((r) => r.data),
  putKguParams: (body: KguParamsWrite) =>
    apiClient.put<KguParams>("/kgu-params", body).then((r) => r.data),

  // Gas curve
  getGasCurve: (date?: string) =>
    apiClient
      .get<GasCurvePoint[]>("/kgu-gas-curve", { params: date ? { date } : undefined })
      .then((r) => r.data),
  putGasCurve: (body: GasCurveWrite) =>
    apiClient.put<GasCurvePoint[]>("/kgu-gas-curve", body).then((r) => r.data),

  // Cost parameters
  // plantId=undefined → скоуп КГУ + загальнофірмові статті; UUID → статті СЕС.
  getCostParameters: (month: string, plantId?: string) =>
    apiClient
      .get<CostParameter[]>("/cost-parameters", {
        params: { month, ...(plantId ? { plant_id: plantId } : {}) },
      })
      .then((r) => r.data),
  putCostParameters: (month: string, items: CostParameterWrite[], plantId?: string) =>
    apiClient
      .put<CostParameter[]>("/cost-parameters", { items }, {
        params: { month, ...(plantId ? { plant_id: plantId } : {}) },
      })
      .then((r) => r.data),
  copyFromPreviousMonth: (month: string) =>
    apiClient
      .post<CostParameter[]>(
        "/cost-parameters/copy-from-previous-month",
        null,
        { params: { month } },
      )
      .then((r) => r.data),
};

// ---------- Gas purchases (передзакуплений газ, FR-PARAMS-05) ----------

export interface GasPurchase {
  id: string;
  tenant_id: string;
  valid_from: string; // YYYY-MM-DD — з 00:00 цієї дати газ доступний
  volume_m3: number | string;
  price_uah_per_m3: number | string;
  notes: string | null;
  created_by: string | null;
  created_at: string;
}

export interface GasPurchaseWrite {
  valid_from: string;
  volume_m3: number;
  price_uah_per_m3: number;
  notes?: string | null;
}

export interface GasBalance {
  from_date: string;
  to_date: string;
  purchased_m3: number | string;
  used_m3: number | string;
  remaining_m3: number | string;
}

/** Спільний пул закупленого газу на місяць: залишок і середньозважена ціна. */
export interface GasPool {
  month: string; // YYYY-MM-01
  as_of: string;
  purchased_m3: number | string;
  remaining_m3: number | string;
  avg_price_uah_per_m3: number | string | null;
}

export const gasPurchasesApi = {
  list: () =>
    apiClient.get<GasPurchase[]>("/gas-purchases").then((r) => r.data),
  create: (body: GasPurchaseWrite) =>
    apiClient.post<GasPurchase>("/gas-purchases", body).then((r) => r.data),
  update: (id: string, body: GasPurchaseWrite) =>
    apiClient.patch<GasPurchase>(`/gas-purchases/${id}`, body).then((r) => r.data),
  remove: (id: string) =>
    apiClient.delete<{ ok: boolean }>(`/gas-purchases/${id}`).then((r) => r.data),
  balance: (from: string, to: string) =>
    apiClient
      .get<GasBalance>("/gas-purchases/balance", { params: { from, to } })
      .then((r) => r.data),
  /** `month` — будь-яка дата місяця (YYYY-MM-DD); без неї — поточний місяць. */
  pool: (month?: string) =>
    apiClient
      .get<GasPool>("/gas-purchases/pool", { params: month ? { month } : {} })
      .then((r) => r.data),
};
